import React from 'react';
import { Calendar, Clock, Copy, CreditCard as Edit, Trash2 } from 'lucide-react';

// Types
interface TimeEntryExtended {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  break_duration: number;
  notes?: string | null;
  hourly_rate?: number | null;
  created_at: string;
  projects?: { name: string | null; hourly_rate?: number | null } | null;
  employees?: { 
    full_name: string | null; 
    name?: string | null;
    hourly_rate?: number | null 
  } | null;
  activities?: { name: string } | null;
}

interface TimeEntryCardProps {
  entry: TimeEntryExtended;
  onEdit: (entry: TimeEntryExtended) => void;
  onDelete: (entryId: string) => void;
  onDuplicate: (entry: TimeEntryExtended) => void;
}

const TimeEntryCard: React.FC<TimeEntryCardProps> = ({
  entry,
  onEdit,
  onDelete,
  onDuplicate,
}) => {
  // Helper function to parse time string
  const parseTimeString = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Calculate hours and earnings
  const calculateHours = () => {
    if (!entry.end_time || !entry.start_time) {
      return { totalMinutes: 0, netMinutes: 0, hours: '00:00', earnings: 0 };
    }
    
    const startMins = parseTimeString(entry.start_time);
    const endMins = parseTimeString(entry.end_time);
    let totalMins = endMins - startMins;
    if (totalMins < 0) totalMins += 24 * 60;
    
    const netMins = Math.max(0, totalMins - (entry.break_duration || 0));
    const h = Math.floor(netMins / 60).toString().padStart(2, '0');
    const m = (netMins % 60).toString().padStart(2, '0');
    
    // Calculate earnings
    const rate = entry.hourly_rate || entry.projects?.hourly_rate || entry.employees?.hourly_rate || 0;
    const earnings = rate > 0 ? (netMins / 60) * rate : 0;
    
    return {
      totalMinutes: totalMins,
      netMinutes: netMins,
      hours: `${h}:${m}`,
      earnings: earnings
    };
  };

  const entryDate = new Date(entry.date);
  const { hours, earnings } = calculateHours();
  
  // Get employee name with fallback
  const employeeName = entry.employees?.full_name || 'Onbekend';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <Calendar className="h-5 w-5 text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {entryDate.toLocaleDateString('nl-NL', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
              })}
            </span>
          </div>
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-gray-400 mr-2" />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {entry.start_time?.substring(0, 5)} - {entry.end_time?.substring(0, 5)}
            </span>
          </div>
          <div className="text-lg font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-md">
            {hours}
          </div>
          {earnings > 0 && (
            <div className="text-sm font-semibold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-md">
              {new Intl.NumberFormat('nl-NL', { 
                style: 'currency', 
                currency: 'EUR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(earnings)}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Project:</span>
          <p className="text-sm text-gray-900 dark:text-white font-medium">{entry.projects?.name || 'Onbekend'}</p>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Medewerker:</span>
          <p className="text-sm text-gray-900 dark:text-white font-medium">{employeeName}</p>
        </div>
        {entry.activities?.name && (
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Activiteit:</span>
            <p className="text-sm text-gray-900 dark:text-white font-medium">{entry.activities.name}</p>
          </div>
        )}
      </div>

      {entry.notes && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 italic bg-gray-50 dark:bg-gray-700/50 p-3 rounded-md border-l-4 border-gray-300 dark:border-gray-600">
          "{entry.notes}"
        </p>
      )}

      <div className="flex justify-between items-center pt-4 border-t border-gray-100 dark:border-gray-700">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {entry.break_duration > 0 && (
        <span>Pauze: {entry.break_duration} min • </span>
        )}
        Aangemaakt: {entry.created_at ? new Date(entry.created_at).toLocaleDateString('nl-NL') : 'Onbekend'}
      </div>
        <div className="flex space-x-2">
          <button
            onClick={() => onDuplicate(entry)}
            className="flex items-center px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-150"
          >
            <Copy className="h-4 w-4 mr-1" />
            Kopiëer
          </button>
          <button
            onClick={() => onEdit(entry)}
            className="flex items-center px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors duration-150"
          >
            <Edit className="h-4 w-4 mr-1" />
            Bewerk
          </button>
          <button
            onClick={() => onDelete(entry.id)}
            className="flex items-center px-3 py-1 text-xs bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 rounded-md hover:bg-red-200 dark:hover:bg-red-800 transition-colors duration-150"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Verwijder
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimeEntryCard;